import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { AlertTriangle, Trash2, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export const DeleteAccount = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDeleteAccount = async () => {
    if (!password) {
      setError('Bitte geben Sie Ihr Passwort ein');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/admin/delete-account`,
        { password },  // ✅ Send as JSON object
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Ihr Account wurde erfolgreich gelöscht');
      console.log(`✅ Account gelöscht: ${response.data.total_deleted} Datensätze entfernt`);
      
      // Logout and redirect
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Account deletion error:', error);
      
      if (error.response?.status === 401) {
        setError('Falsches Passwort');
      } else if (error.response?.status === 403) {
        const detail = error.response.data.detail || 'Dieser Account ist geschützt';
        setError(detail);
        console.error(`🛡️ Account deletion blocked: ${detail}`);
      } else if (error.response?.data?.detail) {
        // Show specific backend error message
        setError(error.response.data.detail);
        console.error(`❌ Deletion failed: ${error.response.data.detail}`);
      } else {
        setError('Fehler beim Löschen des Accounts. Bitte versuchen Sie es später erneut.');
        console.error(`❌ Unknown error: ${error.message}`);
      }
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const handleInitiateDelete = () => {
    if (!password) {
      setError('Bitte geben Sie Ihr Passwort ein');
      return;
    }
    setError('');
    setShowConfirmDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
        {/* Warning Banner */}
        <Alert className="mb-6 border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <AlertDescription className="text-red-900 dark:text-red-100 font-medium">
            <strong>Achtung:</strong> Diese Aktion ist unwiderruflich!
          </AlertDescription>
        </Alert>

        {/* Main Card */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-6 w-6" />
              Account unwiderruflich löschen
            </CardTitle>
            <CardDescription>
              Löschen Sie Ihr AllFlowX-Konto und alle zugehörigen Daten permanent
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Information Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Was wird gelöscht?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Ihr Firmenprofil</strong> und alle Standorte
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Alle Mitarbeiter-Accounts</strong> (Zugang wird sofort deaktiviert)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Operative Daten:</strong> Aufgaben, Temperatur-Logs, MHD-Listen, Backshop-Bestellungen, Abschriften, Unterweisungen
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Finanzielle Daten:</strong> Rechnungen und Abrechnungs-Historie
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span>
                    <strong>Ihr Stripe-Abonnement</strong> wird sofort gekündigt
                  </span>
                </li>
              </ul>
            </div>

            {/* Warning Box */}
            <Alert className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-sm text-yellow-900 dark:text-yellow-100">
                <strong>Wichtig:</strong> Diese Löschung ist DSGVO-konform und kann NICHT rückgängig gemacht werden. 
                Alle Daten werden permanent aus unserer Datenbank entfernt.
              </AlertDescription>
            </Alert>

            {/* Password Input */}
            <div className="space-y-3 pt-4 border-t">
              <Label htmlFor="password">
                Bestätigen Sie mit Ihrem Passwort
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Ihr Passwort"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                disabled={loading}
                className={error ? 'border-red-500' : ''}
              />
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
            </div>

            {/* Delete Button */}
            <Button
              onClick={handleInitiateDelete}
              disabled={loading || !password}
              variant="destructive"
              className="w-full"
              size="lg"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Account unwiderruflich löschen
            </Button>

            {/* Cancel Link */}
            <div className="text-center pt-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                disabled={loading}
              >
                Abbrechen und zurück
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Sind Sie absolut sicher?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  Diese Aktion kann <strong className="text-red-600">NICHT</strong> rückgängig gemacht werden.
                </p>
                <p>
                  Ihr Account <strong>{user?.email}</strong>, alle Standorte, Mitarbeiter und sämtliche Daten werden permanent gelöscht.
                </p>
                <p className="text-sm text-muted-foreground">
                  Ihr Stripe-Abonnement wird automatisch gekündigt.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? 'Wird gelöscht...' : 'Ja, alles löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default DeleteAccount;
